import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import {
  Button, FormControl, InputGroup, Overlay,
} from 'react-bootstrap';
import {
  formatRiseRun,
  parseRiseRun,
  riseToDegrees,
  toFiniteNumberOrNull as toNumberOrNull,
} from '@aerialytic/designer-states/core';
import PropTypes from 'prop-types';

export default function PitchDropdown({
  degrees,
  pitches,
  segIdx,
  pitch,
  noMargin,
  segmentPitchAndAreaHandler,
}) {
  const riseOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const toggleRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);

  const displayValue = useMemo(() => {
    if (segIdx === -1) return undefined; // leave "Set All" input uncontrolled
    if (degrees) return pitch ?? '';
    return formatRiseRun(pitch) || (pitch ?? '');
  }, [degrees, pitch, segIdx]);

  const handleDropdownSelect = useCallback(
    (eventKey) => {
      if (degrees) {
        const idx = Number(eventKey);
        const raw = Array.isArray(pitches) ? pitches[idx] : undefined;
        const value = toNumberOrNull(raw) ?? raw;
    segmentPitchAndAreaHandler(segIdx, value);
        return;
      }

      // In Rise/Run display mode, show 1/12..12/12 and persist equivalent degrees.
      const rise = Number(eventKey);
      const deg = riseToDegrees(rise);
      segmentPitchAndAreaHandler(segIdx, deg ?? '');
    },
    [degrees, pitches, segIdx, segmentPitchAndAreaHandler],
  );

  const handleInputChange = useCallback(
    (e) => {
    const val = e.target.value;
      if (degrees) {
    segmentPitchAndAreaHandler(segIdx, val);
        return;
      }

      const rise = parseRiseRun(val);
      const deg = riseToDegrees(rise);
      segmentPitchAndAreaHandler(segIdx, deg ?? val);
    },
    [degrees, segIdx, segmentPitchAndAreaHandler],
  );

  const items = useMemo(() => {
    if (degrees) {
      return (pitches || []).map((p, idx) => ({
        key: `${p}-${idx}`,
        eventKey: idx,
        label: String(p),
      }));
  }
    return riseOptions.map((rise) => ({
      key: `rise-${rise}`,
      eventKey: rise,
      label: `${rise}/12`,
    }));
  }, [degrees, pitches, riseOptions]);

  const onToggleClick = useCallback(() => {
    setShowMenu((v) => !v);
  }, []);

  const onHide = useCallback(() => {
    setShowMenu(false);
  }, []);

  const popperConfig = useMemo(() => ({
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 4] } },
      { name: 'computeStyles', options: { adaptive: false } },
      { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
      { name: 'flip', enabled: false },
    ],
  }), []);

    return (
      <InputGroup size="sm" className={noMargin ? '' : 'mb-3'}>
      <FormControl
        size="sm"
        value={displayValue}
        onChange={handleInputChange}
        aria-label="Pitch input"
      />
        <InputGroup.Append>
        <Button
          ref={toggleRef}
          className="pitch-dropdown dropdown-toggle dropdown-toggle-split"
            size="sm"
            variant="outline-secondary"
          id={`pitch-dropdown-${segIdx}`}
          aria-haspopup="menu"
          aria-expanded={showMenu}
          onClick={onToggleClick}
        />
        {/* Portal the menu to body to escape any parent stacking-context/overflow */}
        <Overlay
          show={showMenu}
          target={toggleRef.current}
          placement="bottom-end"
          container={typeof document !== 'undefined' ? document.body : undefined}
          rootClose
          onHide={onHide}
          popperConfig={popperConfig}
          >
          {({ placement, ...overlayProps }) => (
            <div
              {...overlayProps}
              role="menu"
              className="dropdown-menu show"
              style={{
                ...(overlayProps.style || {}),
                zIndex: 20000,
                maxHeight: 240,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              data-placement={placement}
            >
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    handleDropdownSelect(it.eventKey);
                    setShowMenu(false);
                  }}
                >
                  {it.label}
                </button>
              ))}
            </div>
          )}
        </Overlay>
        </InputGroup.Append>
      </InputGroup>
    );
}

PitchDropdown.propTypes = {
  degrees: PropTypes.bool.isRequired,
  pitches: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    .isRequired,
  segIdx: PropTypes.number.isRequired,
  pitch: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  noMargin: PropTypes.bool,
  segmentPitchAndAreaHandler: PropTypes.func.isRequired,
};

PitchDropdown.defaultProps = {
  pitch: '',
  noMargin: false,
};
